<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ConfirmOptions } from '@/composables/useConfirm';
import { useBackdropDismiss } from '@/composables/useBackdropDismiss';
import { focusedElement, refocusIfRemoved, useDialogFocus } from '@/composables/useDialogFocus';

// Props
const props = defineProps<{
  request: ConfirmOptions;
}>();

// Emits
const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

const { t } = useI18n();

// State
const overlay = ref<HTMLElement | null>(null);
const confirmButton = ref<HTMLButtonElement | null>(null);
const opener = focusedElement();
let isConfirmed = false;

// Computed
const confirmButtonClass = computed(() => {
  switch (props.request.severity) {
    case 'danger':
      return 'btn-danger';
    case 'warning':
      return 'btn-warning';
    default:
      return 'btn-primary';
  }
});

// Methods
const cancel = () => emit('cancel');

const confirm = () => {
  isConfirmed = true;
  emit('confirm');
};

// Backdrop press+release guard: cancel only when both land on the overlay so a
// selection drag ending on the backdrop doesn't dismiss the dialog (CORA-264 #2).
const { onMousedown: onBackdropMousedown, onClick: onBackdropClick } = useBackdropDismiss(cancel);

useDialogFocus(overlay, { onEscape: cancel });
onMounted(() => confirmButton.value?.focus());
// Registered after useDialogFocus, so focus is back on the opener, which the confirmed action may now remove.
onBeforeUnmount(() => {
  if (isConfirmed && opener) refocusIfRemoved(opener, props.request.fallbackFocus);
});
</script>

<template>
  <div
    ref="overlay"
    class="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    @mousedown="onBackdropMousedown"
    @click="onBackdropClick"
  >
    <div class="w-full max-w-md rounded-xl bg-card shadow-2xl">
      <div class="p-6">
        <h3 v-if="request.title" class="mb-2 text-lg font-semibold text-ink">{{ request.title }}</h3>
        <p class="whitespace-pre-line text-ink-muted">{{ request.message }}</p>
      </div>
      <div class="flex justify-end gap-3 border-t border-[color:var(--color-border)] bg-surface-muted p-4">
        <button type="button" class="btn-outline" data-test="confirm-dialog-cancel" @click="cancel">
          {{ request.cancelLabel || t('common.cancel') }}
        </button>
        <button ref="confirmButton" type="button" :class="confirmButtonClass" data-test="confirm-dialog-confirm" @click="confirm">
          {{ request.confirmLabel || t('common.confirm') }}
        </button>
      </div>
    </div>
  </div>
</template>
